import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { channelPartnerAuthAPI } from '../../services/api';
import { useChannelPartnerAuth } from '../../contexts/ChannelPartnerAuthContext';
import { Card, Button, Spinner, Badge, EmptyState } from '../../components/UI';
import {
  HiOutlineClipboardCopy,
  HiOutlineCurrencyRupee,
  HiOutlineCash,
  HiOutlineClock,
  HiOutlineSpeakerphone,
  HiOutlineMail,
} from 'react-icons/hi';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const statusVariant = (status) => (status === 'paid' ? 'success' : status === 'failed' ? 'danger' : 'warning');

const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error('Failed to copy — please copy it manually');
  }
};

const PartnerDashboard = () => {
  const { partner } = useChannelPartnerAuth();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setInviteSending(true);
    try {
      const res = await channelPartnerAuthAPI.sendInvite(inviteEmail.trim());
      toast.success(res.data?.message || 'Invite sent');
      setInviteEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invite');
    } finally {
      setInviteSending(false);
    }
  };

  const { data: ledgerData, isLoading } = useQuery({
    queryKey: ['partner-commissions'],
    queryFn: () => channelPartnerAuthAPI.getCommissions({ limit: 100 }),
    enabled: !!partner,
  });

  const { data: noticesData, isLoading: noticesLoading } = useQuery({
    queryKey: ['partner-notices'],
    queryFn: () => channelPartnerAuthAPI.getNotices(),
    enabled: !!partner,
  });

  const commissions = ledgerData?.data?.data || [];
  const notices = noticesData?.data?.data || [];
  const referralLink = partner ? `${window.location.origin}/r/${partner.linkHash}` : '';

  const commissionSummary = partner
    ? partner.commissionType === 'percentage'
      ? `${partner.commissionValue}% commission`
      : `${inr(partner.commissionValue)} fixed commission`
    : '';

  if (!partner) return null;

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-500 to-indigo-600 p-6 sm:p-8 text-white shadow-lg">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute -right-2 bottom-0 w-24 h-24 rounded-full bg-white/10" />
        <div className="relative">
          <h1 className="text-xl sm:text-2xl font-bold">Welcome, {partner.name}</h1>
          <p className="text-sm sm:text-base text-white/90 mt-1 max-w-xl">
            {commissionSummary} · {partner.commissionRecurrence === 'recurring' ? 'Every payment' : 'First payment only'}
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
            <HiOutlineCurrencyRupee className="w-5 h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider">Total Earned</p>
            <p className="text-lg font-bold text-secondary-900">{inr(partner.totalEarned)}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-success-50 flex items-center justify-center flex-shrink-0">
            <HiOutlineCash className="w-5 h-5 text-success-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider">Total Paid</p>
            <p className="text-lg font-bold text-secondary-900">{inr(partner.totalPaid)}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-warning-50 flex items-center justify-center flex-shrink-0">
            <HiOutlineClock className="w-5 h-5 text-warning-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider">Pending Payout</p>
            <p className="text-lg font-bold text-secondary-900">{inr((partner.totalEarned || 0) - (partner.totalPaid || 0))}</p>
          </div>
        </Card>
      </div>

      {/* Referral link */}
      <Card>
        <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3">Your Referral Link</p>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              readOnly
              value={referralLink}
              className="flex-1 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-sm text-secondary-700"
            />
            <Button variant="outline" onClick={() => copyToClipboard(referralLink, 'Referral link')} icon={<HiOutlineClipboardCopy className="w-4 h-4" />}>
              Copy Link
            </Button>
          </div>
          <div className="flex items-center gap-2 text-sm text-secondary-500">
            <span>Or share just the code:</span>
            <button
              type="button"
              onClick={() => copyToClipboard(partner.token, 'Partner code')}
              className="font-mono font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded hover:bg-primary-100"
            >
              {partner.token}
            </button>
          </div>
        </div>
        <p className="text-xs text-secondary-400 mt-3">
          Share this link — when someone registers and pays through it, you earn commission automatically.
        </p>
      </Card>

      {/* Invite via email */}
      <Card>
        <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3">Invite via Email</p>
        <form onSubmit={handleSendInvite} className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="lead@company.com"
            disabled={inviteSending}
            className="flex-1 rounded-lg border border-secondary-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-secondary-50"
          />
          <Button type="submit" variant="primary" loading={inviteSending} disabled={inviteSending} icon={<HiOutlineMail className="w-4 h-4" />}>
            Send Invite
          </Button>
        </form>
        <p className="text-xs text-secondary-400 mt-3">
          We'll email them your referral link directly — no need to copy and paste it yourself.
        </p>
      </Card>

      {/* Notice Board — messages from the TeleValley team */}
      <Card>
        <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3">Notice Board</p>
        {noticesLoading ? (
          <div className="flex items-center justify-center py-6"><Spinner /></div>
        ) : notices.length === 0 ? (
          <EmptyState
            icon={<HiOutlineSpeakerphone className="w-7 h-7 text-secondary-400" />}
            title="No notices yet"
            description="Messages from the TeleValley team will show up here."
          />
        ) : (
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {notices.map((n) => (
              <div key={n._id} className="flex gap-3 rounded-xl border border-secondary-100 bg-secondary-50 p-3">
                <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <HiOutlineSpeakerphone className="w-4 h-4 text-primary-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-secondary-800">{n.message}</p>
                  <p className="text-xs text-secondary-400 mt-1">{formatDate(n.createdAt)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Commission history */}
      <Card>
        <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3">Commission History</p>
        {isLoading ? (
          <div className="flex items-center justify-center py-8"><Spinner /></div>
        ) : commissions.length === 0 ? (
          <EmptyState
            icon={<HiOutlineCurrencyRupee className="w-7 h-7 text-secondary-400" />}
            title="No commissions yet"
            description="Once someone you referred pays for a plan, it will show up here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-secondary-100 text-left">
                  <th className="py-2 pr-4 text-xs font-bold text-secondary-500 uppercase tracking-wider">Organization</th>
                  <th className="py-2 pr-4 text-xs font-bold text-secondary-500 uppercase tracking-wider">Amount</th>
                  <th className="py-2 pr-4 text-xs font-bold text-secondary-500 uppercase tracking-wider">Status</th>
                  <th className="py-2 pr-4 text-xs font-bold text-secondary-500 uppercase tracking-wider">Paid</th>
                  <th className="py-2 text-xs font-bold text-secondary-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {commissions.map((c) => (
                  <tr key={c._id}>
                    <td className="py-3 pr-4 text-secondary-900 font-medium">{c.organizationId?.name || c.organizationName || '—'}</td>
                    <td className="py-3 pr-4 text-secondary-700">{inr(c.amount)}</td>
                    <td className="py-3 pr-4">
                      <Badge variant={statusVariant(c.status)}>{c.status}</Badge>
                    </td>
                    <td className="py-3 pr-4 text-secondary-700">{c.status === 'paid' && c.amountPaid != null ? inr(c.amountPaid) : '—'}</td>
                    <td className="py-3 text-secondary-500">{formatDate(c.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PartnerDashboard;
